import { motion } from 'framer-motion';
import { IoIosAlert } from "react-icons/io";
import { useEffect, useRef, useMemo, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { TextField, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PatternFormat } from 'react-number-format';
import dayjs from 'dayjs';
import { translate } from 'google-translate-api-browser';  // Import the translate function
import API from '../Common/API';

const FieldInput = ({
    type = "text", label, fieldProps, injectClass, disabled = false, fieldHelper, fieldMeta, onChange, autoComplete = "true", placeholder,
    edit, dataLoaded = false, ml, length, labelInjectClass, minDate, maxDate, instruction = null, onEnter, outerInjectClass, required = false,
    isRequired = false, dateInjectClass, translate
}) => {
    const input = useRef(null);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const [showError, setShowError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [translatedText, setTranslatedText] = useState('');  // State to store translated text
    const [actualValue, setActualValue] = useState('')

    const handleFocus = () => {
        if (input.current !== null && type !== 'phone') {
            input.current.focus();
        }
    };

    const handleInputChange = (e) => {
        if (type === 'file' && typeof onChange === 'function') {
            if (fieldHelper && typeof fieldHelper.setValue === 'function') {
                fieldHelper.setValue(e.target.value);
                fieldHelper.setTouched(true);
            }
            setTimeout(() => {
                onChange(e.target.files[0]);
            }, 100);
        } else if (typeof onChange === 'function') {
            onChange(e.target.value);
        } else {
            fieldHelper.setValue(e.target.value);
        }
    };

    const handleEnterKey = (event) => {
        if (event.key === 'Enter' && typeof onEnter === 'function') {
            onEnter(event.target.value);
        }
    };

    const inputType = useMemo(() => {
        if (type === "password") return showPassword ? "text" : "password";
        return type;
    }, [type, showPassword]);

    const inputChange = (e) => {
        if (type === 'file' && typeof onChange === 'function') {
            if (fieldHelper && typeof fieldHelper.setValue === 'function') {
                fieldHelper.setValue(e.target.value);
                fieldHelper.setTouched(true);
            }
            setTimeout(() => {
                onChange(e.target.files[0]);
            }, 100);
        } else if (typeof onChange === 'function') {
            onChange(e.target.value);
        }

        if (translate) {
            translateFormHandling(e.target.value);
        }
    };

    const toggleError = (val) => setShowError(val);

    useEffect(() => {
        if (type === 'file' && fieldMeta && fieldMeta.touched === false) {
            input.current.value = null;
            fieldHelper.setValue('');
        }
    }, [fieldMeta.touched, fieldProps.value]); // eslint-disable-line

    const enterFunction = (event) => {
        if (event.key === 'Enter') {
            onEnter(event.target.value);
        }
    };

    const handleDateChange = (newValue) => {
        const formattedDate = dayjs(newValue).format('YYYY-MM-DD');
        fieldHelper.setValue(formattedDate); // Set the formatted date in Formik state
        fieldHelper.setTouched(true);

        if (typeof onChange === 'function') {
            onChange(formattedDate);
        }
    };

    // Function to handle translation
    // const handleTranslation = async (text) => {
    //     if (translate) {
    //         try {
    //             const result = await translate(text, { to: translate });
    //             setTranslatedText(result.text);  // Update translated text
    //         } catch (error) {
    //             console.error("Translation error:", error);
    //         }
    //     }
    // };

    // useEffect(() => {
    //     if (fieldProps.value) {
    //         handleTranslation(fieldProps.value);  // Translate the input text when it's updated
    //     }
    // }, [fieldProps.value, translate]);

    const translateFormHandling = async (text) => {
        setTranslatedText('')
        setActualValue(text)
        if (text.toString().length > 0) {
            try {
                const res = await API.post('/common/translate', { text: text });
                if (res.status === 200) {
                    const tText = res.data.data.translatedText;
                    setTranslatedText(tText);  // Update translated text
                }
            } catch (error) {
                console.error("Translation API Error:", error);
            }
        } else {
            setTranslatedText('')
        }
    };

    return (
        <motion.div className={`relative mt-2 mb-2 p-5 ${outerInjectClass}`}>
            {
                inputType === 'date' ? <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <motion.div
                        className={`text-sm absolute -top-3 left-2 drop-shadow-sm rounded w-auto text-tPColor ${labelInjectClass ? labelInjectClass : 'px-5 '}`}
                    >
                        {label} {required && <span className="text-[red]">*</span>}
                    </motion.div>
                    <div className="relative w-full h-fit flex items-center ">
                        <DatePicker
                            value={fieldProps.value ? dayjs(fieldProps.value) : null}
                            onChange={handleDateChange}
                            className={dateInjectClass}
                            minDate={minDate}
                            maxDate={maxDate}
                            format='DD/MM/YYYY'
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    required={isRequired}
                                    error={Boolean(fieldMeta.error && fieldMeta.touched)}
                                    helperText={fieldMeta.touched && fieldMeta.error ? fieldMeta.error : ''}
                                    variant="standard"
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                </LocalizationProvider> : inputType === 'textarea' ? 
                <motion.div className={`relative ${edit && 'ring-1'} ${injectClass} drop-shadow-lg ring-[lightgrey] rounded ${type === 'textarea' ? 'h-auto' : 'h-11 '} px-2 py-2 w-full flex flex-row ${disabled ? 'bg-primeColor/20' : ''}`} onClick={handleFocus}>
                    <TextField
                    type={inputType}
                    label={label}
                    multiline
                    rows={4}
                    required={isRequired}
                    variant="standard"
                    fullWidth
                    className='ring-[lightgrey]'
                    inputRef={inputRef}
                    disabled={!edit || disabled}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    InputProps={{
                        endAdornment: type === "password" && (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        startAdornment: type === "phone" && (
                            <PatternFormat
                                customInput={TextField}
                                format="##########"
                                {...fieldProps}
                                disabled={disabled}
                            />
                        ),
                        inputProps: {
                            maxLength: ml && length,
                            onKeyDown: handleEnterKey,
                        },
                    }}
                    {...fieldProps}
                    onChange={handleInputChange}
                /> 
                {showError && <motion.div className={'absolute flex top-0 right-8 w-auto px-5 rounded py-2 h-7 text-center align-center items-center text-[red]'}>{fieldMeta.error}</motion.div>}
                            {edit && fieldMeta.error && fieldMeta.touched && <IoIosAlert className='w-8 cursor-pointer text-[red]' onMouseEnter={() => toggleError(true)} onMouseLeave={() => toggleError(false)} />}
                    </motion.div>: <>
                    <motion.div className={`text-sm absolute -top-3 left-2 drop-shadow-sm rounded w-auto text-tPColor ${labelInjectClass ? labelInjectClass : 'px-5 '}`}>
                        {label} {required && <span className="text-[red]">*</span>}
                    </motion.div>
                    <motion.div className={`relative ${edit && 'ring-1'} ${injectClass} drop-shadow-lg ring-[lightgrey] rounded ${type === 'textarea' ? 'h-auto' : 'h-11 '} px-2 py-2 w-full flex flex-col ${disabled ? 'bg-primeColor/20' : ''}`} onClick={handleFocus}>
                        <motion.div ref={wrapRef} className={`${type === 'textarea' ? 'h-auto' : 'h-9'} ${disabled ? 'bg-primeColor/20' : ''} w-full flex flex-row`}>
                            {type === 'phone' ? <PatternFormat disabled={disabled} className={`pb-1.5 pl-3 bg-transparent pr-10 w-full outline-none`} ref={input} {...fieldProps} displayType={'input'} format="##########"/> :
                                type === 'date' ? <input type={'date'} ref={input} className={`w-full px-2 focus:outline-none bg-transparent ${injectClass}`}  {...fieldProps} autoComplete={autoComplete} placeholder={placeholder} disabled={disabled} min={minDate} max={maxDate} onInput={inputChange} />
                                    : type === 'textarea' ? <textarea rows={5} ref={input} className={`w-full px-2 focus:outline-none bg-transparent ${injectClass}`} maxLength={ml && length} {...fieldProps} autoComplete={autoComplete} placeholder={placeholder} disabled={!edit || disabled} onKeyUp={inputChange} onKeyDown={(ev) => typeof onEnter === 'function' && enterFunction(ev)} ></textarea> :
                                        type === 'file' ? <input type={'file'} ref={input} className={`w-full px-2 focus:outline-none bg-transparent ${injectClass}`} maxLength={ml && length} {...fieldProps} autoComplete={autoComplete} placeholder={placeholder} disabled={!edit || disabled} onChange={inputChange} /> :
                                            <input type={inputType} ref={input} className={`w-full px-2 focus:outline-none ${disabled ? 'bg-primeColor/20' : 'bg-transparent'} ${injectClass}`} maxLength={ml && length} {...fieldProps} autoComplete={autoComplete} placeholder={placeholder} disabled={!edit || disabled} onKeyUp={inputChange} onKeyDown={(ev) => typeof onEnter === 'function' && enterFunction(ev)} />}
                            {type === 'password' && !showPassword && <FaEyeSlash className='my-1.5' onClick={() => setShowPassword(true)} />}
                            {type === 'password' && showPassword && <FaEye className='my-1.5' onClick={() => setShowPassword(false)} />}
                            {showError && <motion.div className={'absolute flex top-0 right-8 w-auto px-5 rounded py-2 h-7 text-center align-center items-center text-[red]'}>{fieldMeta.error}</motion.div>}
                            {edit && fieldMeta.error && fieldMeta.touched && <IoIosAlert className='w-8 cursor-pointer text-[red]' onMouseEnter={() => toggleError(true)} onMouseLeave={() => toggleError(false)} />}
                        </motion.div>
                    </motion.div>
                </>
            }
            {instruction && <p className='text-xs font-bold py-1'>{instruction}</p>}
            {translate && translatedText &&
                <motion.div className="w-full h-fit flex flex-col px-2 py-2 bg-white text-black">
                    <p className='font-bold text-xl text-primeColor cursor-pointer' onClick={() => { if (typeof onChange === 'function') { onChange({ translatedValue: translatedText, value: actualValue }); setTranslatedText('') } else { fieldHelper.setValue(translatedText); setTranslatedText('') } }}>{translatedText}</p>
                </motion.div>}
        </motion.div>
    );
};

export default FieldInput;